import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useContextSelector } from "use-context-selector";
import { FaArrowLeft, FaCheckCircle, FaEye, FaEyeSlash } from "react-icons/fa";
import { toast } from "react-toastify";
import AuthContext from "../context/AuthContext";
import { apiPost } from "../Utils/apiFetch";
import apiPath from "../Utils/apiPath";
import { getLoginWebsite, getReadableApiMessage } from "../Utils/authPresentation";
import Layout from "./Layout";
import "./AffiliateRegistration.css";

export default function AffiliateRegistration() {
  const { t } = useTranslation();
  const copy = key => t(`affiliateRegistration.${key}`);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const affiliate = useContextSelector(AuthContext, ctx => ctx.affiliate);
  const setAffiliate = useContextSelector(AuthContext, ctx => ctx.setAffiliate);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const referralCode = new URLSearchParams(search).get("referral_code");
  const registrationComplete = pathname === "/affilates";
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onBlur",
    shouldFocusError: true,
    defaultValues: { username: "", password: "", confirmPassword: "", mobile: "", referral_code: "", acceptTerms: false },
  });
  const password = watch("password");

  useEffect(() => {
    if (!registrationComplete) setValue("referral_code", referralCode || "");
  }, [referralCode, registrationComplete, setValue]);

  useEffect(() => {
    if (getValues("confirmPassword")) void trigger("confirmPassword");
  }, [password, getValues, trigger]);

  const onSubmit = async body => {
    try {
      const { status, data } = await apiPost(apiPath.affiliate, {
        whatsappNumber: body.mobile,
        username: body.username,
        password: body.password,
        confirmPassword: body.confirmPassword,
        referral_code: body.referral_code || "",
        website: getLoginWebsite(window.location.hostname),
      });
      if (status !== 200 || !data?.success) {
        toast.error(getReadableApiMessage(data?.message, copy("failure")));
        return;
      }
      const createdCode = data.results?.adminuser?.referalCode || "";
      setAffiliate(createdCode);
      toast.success(getReadableApiMessage(data.message, copy("success")));
      navigate(`/affilates${createdCode ? `?referral_code=${encodeURIComponent(createdCode)}` : ""}`, { replace: true });
    } catch (error) {
      toast.error(getReadableApiMessage(error.response?.data?.message, copy("failure")));
    }
  };

  const fieldError = name => errors[name] ? (
    <p className="chorki-affiliate-error" id={`affiliate-${name}-error`} role="alert">{errors[name].message}</p>
  ) : null;
  const accessibility = name => ({
    "aria-invalid": Boolean(errors[name]),
    "aria-describedby": errors[name] ? `affiliate-${name}-error` : undefined,
  });

  return (
    <Layout>
      <main className="chorki-affiliate-page br_main">
        <section className="chorki-affiliate-card" aria-labelledby="affiliate-page-title">
          <Link className="chorki-affiliate-back" to="/"><FaArrowLeft aria-hidden="true" />{t("Home")}</Link>
          <header className="chorki-affiliate-header">
            <img src="/assets/chorki/logo.svg" alt="Chorki" />
            <span className="chorki-affiliate-badge">{t("AFFILIATE")}</span>
            <h1 id="affiliate-page-title">{copy(registrationComplete ? "successTitle" : "title")}</h1>
            <p>{copy(registrationComplete ? "successDescription" : "description")}</p>
          </header>

          {registrationComplete ? (
            <div className="chorki-affiliate-success" role="status">
              <FaCheckCircle aria-hidden="true" />
              {(affiliate || referralCode) && (
                <div className="chorki-affiliate-code"><span>{copy("yourReferralCode")}</span><strong>{affiliate || referralCode}</strong></div>
              )}
              <Link className="chorki-affiliate-submit" to="/">{copy("returnHome")}</Link>
            </div>
          ) : (
            <form className="chorki-affiliate-form" onSubmit={handleSubmit(onSubmit)} noValidate aria-busy={isSubmitting}>
              <fieldset disabled={isSubmitting}>
                <div className="chorki-affiliate-field">
                  <label htmlFor="affiliate-username">{t("Username")} <span aria-hidden="true">*</span></label>
                  <input id="affiliate-username" type="text" autoComplete="username" spellCheck={false} placeholder={copy("usernamePlaceholder")} aria-required="true" {...accessibility("username")} {...register("username", {
                    required: t("Enter_Valid_Username"),
                    validate: {
                      noLeadingSpaces: value => !/^\s/.test(value) || t("Enter_Valid_Username"),
                      noTrailingSpaces: value => !/\s$/.test(value) || t("Enter_Valid_Username"),
                      alphanumericOnly: value => /^[a-zA-Z0-9 ]*$/.test(value) || copy("usernameCharacters"),
                      noOnlySpaces: value => value.trim().length > 0 || t("Enter_Valid_Username"),
                      mustContainAlphabet: value => /[a-zA-Z]/.test(value) || copy("usernameLetter"),
                      minLength: value => value.trim().length >= 3 || t("Min3User"),
                      maxLength: value => value.trim().length <= 30 || t("Max30User"),
                    },
                  })} />
                  {fieldError("username")}
                </div>

                <div className="chorki-affiliate-field">
                  <label htmlFor="affiliate-password">{t("Password")} <span aria-hidden="true">*</span></label>
                  <div className="chorki-affiliate-password">
                    <input id="affiliate-password" type={showPassword ? "text" : "password"} autoComplete="new-password" placeholder={copy("passwordPlaceholder")} aria-required="true" {...accessibility("password")} {...register("password", {
                      required: t("Enter_Password"),
                      minLength: { value: 6, message: t("PasswordMin") },
                      maxLength: { value: 20, message: t("PasswordMax") },
                      validate: {
                        hasAlphabet: value => /[a-zA-Z]/.test(value) || copy("passwordLetter"),
                        hasNumber: value => /[0-9]/.test(value) || copy("passwordNumber"),
                      },
                    })} />
                    <button type="button" onClick={() => setShowPassword(current => !current)} aria-label={copy(showPassword ? "hidePassword" : "showPassword")} aria-pressed={showPassword}>
                      {showPassword ? <FaEyeSlash aria-hidden="true" /> : <FaEye aria-hidden="true" />}
                    </button>
                  </div>
                  {fieldError("password")}
                </div>

                <div className="chorki-affiliate-field">
                  <label htmlFor="affiliate-confirm-password">{t("Confirm_Password")} <span aria-hidden="true">*</span></label>
                  <div className="chorki-affiliate-password">
                    <input id="affiliate-confirm-password" type={showConfirmPassword ? "text" : "password"} autoComplete="new-password" placeholder={t("Confirm_Password")} aria-required="true" {...accessibility("confirmPassword")} {...register("confirmPassword", {
                      required: t("Enter_CPassword"),
                      validate: value => value === getValues("password") || t("PasswordMatch"),
                    })} />
                    <button type="button" onClick={() => setShowConfirmPassword(current => !current)} aria-label={copy(showConfirmPassword ? "hideConfirmPassword" : "showConfirmPassword")} aria-pressed={showConfirmPassword}>
                      {showConfirmPassword ? <FaEyeSlash aria-hidden="true" /> : <FaEye aria-hidden="true" />}
                    </button>
                  </div>
                  {fieldError("confirmPassword")}
                </div>

                <details className="chorki-affiliate-tips">
                  <summary>{t("Passwordrequirements")}</summary>
                  <ul><li>{copy("passwordLength")}</li><li>{copy("passwordLetter")}</li><li>{copy("passwordNumber")}</li></ul>
                </details>

                <div className="chorki-affiliate-field">
                  <label htmlFor="affiliate-mobile">{copy("whatsappNumber")} <span aria-hidden="true">*</span></label>
                  <input id="affiliate-mobile" type="tel" inputMode="numeric" autoComplete="tel" placeholder={copy("phonePlaceholder")} aria-required="true" {...accessibility("mobile")} {...register("mobile", {
                    required: copy("phoneRequired"),
                    minLength: { value: 10, message: t("MobileMin") },
                    maxLength: { value: 12, message: t("MobileMax") },
                    pattern: { value: /^\d+$/, message: copy("phoneDigits") },
                  })} />
                  {fieldError("mobile")}
                </div>

                <div className="chorki-affiliate-field">
                  <label htmlFor="affiliate-referral-code">{copy("referralCode")} <small>{copy("optional")}</small></label>
                  <input id="affiliate-referral-code" type="text" autoComplete="off" placeholder={copy("referralPlaceholder")} {...register("referral_code")} />
                </div>

                <label className="chorki-affiliate-terms" htmlFor="affiliate-terms">
                  <input id="affiliate-terms" type="checkbox" {...register("acceptTerms")} />
                  <span>{t("Iagreeunderstandthe")} <Link to="/terms" target="_blank" rel="noopener noreferrer">{copy("terms")}</Link></span>
                </label>
                <button className="chorki-affiliate-submit" type="submit" disabled={isSubmitting}>{isSubmitting ? copy("submitting") : t("Submit")}</button>
              </fieldset>
            </form>
          )}
        </section>
      </main>
    </Layout>
  );
}
